/**
 * Pure item text helpers (names, stat lines) shared by tooltips, lists and the loot reveal.
 * Items have no names in the progression model, so non-legendary names are generated
 * deterministically from the item id (same item → same name everywhere).
 */
import type { Item, ItemSlot, SecondaryStat } from '@keystone/progression';
import { MASTERWORK_MAX, RATING_PER_PERCENT, effectiveIlvl, legendary, tierPieceName, tierSet, trinketEffect } from '@keystone/progression';

export const SLOT_LABEL: Readonly<Record<ItemSlot, string>> = {
  weapon: 'Weapon',
  helm: 'Helm',
  chest: 'Chest',
  legs: 'Legs',
  gloves: 'Gloves',
  boots: 'Boots',
  ring: 'Ring',
  trinket: 'Trinket',
};

export const STAT_LABEL: Readonly<Record<SecondaryStat, string>> = { haste: 'Haste', crit: 'Critical Strike', expertise: 'Expertise', spirit: 'Spirit' };

const NOUNS: Readonly<Record<ItemSlot, readonly string[]>> = {
  weapon: ['Blade', 'Edge', 'Warbrand', 'Fang', 'Reaver'],
  helm: ['Helm', 'Visage', 'Crown', 'Coif', 'Greathelm'],
  chest: ['Breastplate', 'Hauberk', 'Vestments', 'Cuirass', 'Raiment'],
  legs: ['Legguards', 'Greaves', 'Leggings', 'Tassets', 'Breeches'],
  gloves: ['Gauntlets', 'Grips', 'Handguards', 'Wraps', 'Gloves'],
  boots: ['Sabatons', 'Treads', 'Striders', 'Boots', 'Footguards'],
  ring: ['Band', 'Loop', 'Signet', 'Seal', 'Coil'],
  trinket: ['Idol', 'Charm', 'Talisman', 'Totem', 'Relic'],
};
const PREFIX: readonly string[] = ['Venomforged', 'Fanged', 'Altar-Blessed', 'Serpentscale', 'Gilded', 'Stormbound', 'Emberlit', 'Wardwoven', 'Duskwrought', 'Keystone'];
const STARTER_PREFIX = 'Initiate’s';

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Display name: legendary / trinket effect name, else a deterministic "Prefix Noun". */
export function itemName(item: Item): string {
  if (item.legendaryId) return legendary(item.legendaryId)?.name ?? 'Unknown Legendary';
  if (item.setId) {
    const n = tierPieceName(item.setId, item.slot);
    if (n) return n;
  }
  if (item.trinketEffectId) {
    const t = trinketEffect(item.trinketEffectId);
    if (t) return t.name;
  }
  const h = hash(item.id);
  const nouns = NOUNS[item.slot] ?? NOUNS.trinket;
  const noun = nouns[h % nouns.length]!;
  if (item.source === 'starter') return `${STARTER_PREFIX} ${noun}`;
  return `${PREFIX[(h >>> 8) % PREFIX.length]!} ${noun}`;
}

/** "+42 Haste (2.1%)", with the stat quality when it is not 100% ("· 104%"). */
export function secondaryLine(r: { stat: SecondaryStat; rating: number; quality?: number }): string {
  const pct = r.rating / RATING_PER_PERCENT;
  const q = r.quality !== undefined && Math.abs(r.quality - 1) > 0.001 ? ` · ${Math.round(r.quality * 100)}% quality` : '';
  return `+${Math.round(r.rating)} ${STAT_LABEL[r.stat]} (${pct.toFixed(1)}%)${q}`;
}

export interface ItemTextLine {
  text: string;
  kind: 'stat' | 'socket' | 'effect' | 'meta' | 'flavor' | 'warn';
  /** Optional colour hint (gem colour, reforged marker). */
  tone?: string;
}

/** Everything a tooltip shows, as data (testable, reusable by other views). */
export function itemTextLines(item: Item): ItemTextLine[] {
  const out: ItemTextLine[] = [];
  item.secondaries.forEach((r, i) => {
    const reforged = item.reforged?.index === i;
    const empowered = item.forge?.empowered?.includes(i);
    out.push({ text: secondaryLine(r) + (reforged ? '  (reforged)' : '') + (empowered ? '  (Empowered)' : ''), kind: 'stat', tone: reforged ? 'reforged' : empowered ? 'empowered' : undefined });
  });
  for (let i = 0; i < item.sockets; i++) {
    const g = item.gems[i];
    out.push(g ? { text: `${g.color[0]!.toUpperCase()}${g.color.slice(1)} rank ${g.rank}`, kind: 'socket', tone: g.color } : { text: 'Empty socket', kind: 'socket', tone: 'empty' });
  }
  if (item.legendaryId) {
    const l = legendary(item.legendaryId);
    if (l) out.push({ text: l.description, kind: 'effect' });
  }
  if (item.trinketEffectId) {
    const t = trinketEffect(item.trinketEffectId);
    if (t) out.push({ text: t.description, kind: 'effect' });
  }
  if (item.setId) {
    const set = tierSet(item.setId);
    if (set) out.push({ text: `Tier set: ${set.name}`, kind: 'effect', tone: 'set' });
  }
  const mw = item.masterwork ?? 0;
  if (mw > 0) out.push({ text: `Masterwork ${'★'.repeat(mw)}${'☆'.repeat(Math.max(0, MASTERWORK_MAX - mw))}`, kind: 'meta' });
  const f = item.forge;
  if (f && f.resonance > 0) out.push({ text: `Resonance ${f.resonance}% · +${f.fails * 10}% failstack`, kind: 'meta', tone: 'resonance' });
  if (f?.gilding) out.push({ text: `Gilded ${'✦'.repeat(f.gilding)}`, kind: 'meta', tone: 'gilded' });
  return out;
}

/** Effective item level (masterwork included) for badges. */
export function itemLevel(item: Item): number {
  return effectiveIlvl(item);
}
